// Dashboard tab — the default child route of /student/competition/[id]/*.
//
// Shared state (competition, my team, deliverables, leaderboard,
// announcements) comes from useCompetitionShell — _layout.tsx fetches it
// once and keeps it mounted across tab swaps, so navigating between tabs
// no longer flashes the case-progress visuals back to zero.
//
// Local to this file: the announcements overlay open/close state and the
// Register / dashboard fallback CTA when the user isn't registered yet.

import type { Href } from "expo-router";
import { router } from "expo-router";
import { useState } from "react";
import { ScrollView, Text, View } from "react-native";

import { CompactWorkspaceHero } from "../../../../../components/student/dashboard/CompactWorkspaceHero";
import { ProgramAnnouncementsOverlay } from "../../../../../components/student/dashboard/ProgramAnnouncementsOverlay";
import { ProgramDashboard } from "../../../../../components/student/dashboard/ProgramDashboard";
import { Button, Card } from "../../../../../components/ui";
import { useCompetitionShell } from "../../../../../components/student/CompetitionShellContext";
import { markAnnouncementViewed } from "../../../../../lib/announcements";
import { registerForCompetition } from "../../../../../lib/registrations";

export default function StudentCompetitionDashboardScreen() {
  const {
    competitionId,
    competition,
    registered,
    hasLoaded,
    eligible,
    myTeam,
    deliverables,
    teamSubmissions,
    teamStatuses,
    leaderboardRows,
    announcements,
    setAnnouncements,
    busy,
    setBusy,
    actionErr,
    setActionErr,
    refresh,
  } = useCompetitionShell();

  const [announcementsOverlayOpen, setAnnouncementsOverlayOpen] = useState(false);

  if (!competition) return null;
  const row = competition;
  const ownTeamRow = myTeam
    ? leaderboardRows.find((r) => r.team_id === myTeam.team.id) ?? null
    : null;

  async function onRegister() {
    if (!competitionId) return;
    setBusy(true);
    setActionErr(null);
    const { error } = await registerForCompetition(competitionId);
    setBusy(false);
    if (error) {
      setActionErr(error.message);
      return;
    }
    await refresh();
  }

  return (
    <>
      <ScrollView className="flex-1 pb-10" keyboardShouldPersistTaps="handled">
        <CompactWorkspaceHero
          companyName={row.company_name ?? "Company"}
          programName={row.name}
          opensAt={row.opens_at}
          closesAt={row.closes_at}
          status={row.status}
          teamSizeMin={row.team_size_min}
          teamSizeMax={row.max_team_size}
          deliverablesCount={deliverables.length}
          submittedCount={teamSubmissions.length}
          nextDueAt={
            [...deliverables]
              .filter((d) => !teamSubmissions.some((s) => s.deliverable_id === d.id))
              .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))[0]?.due_at ?? null
          }
        />

        {registered ? (
          <View className="px-4 pb-6 pt-2 md:px-8">
            {actionErr ? (
              <Text className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {actionErr}
              </Text>
            ) : null}
            <ProgramDashboard
              competition={{
                id: row.id,
                status: row.status,
                opens_at: row.opens_at,
                closes_at: row.closes_at,
                max_team_size: row.max_team_size,
              }}
              myTeam={myTeam}
              deliverables={deliverables}
              submissions={teamSubmissions}
              statuses={teamStatuses}
              leaderboard={leaderboardRows}
              ownTeamRow={ownTeamRow}
              announcements={announcements}
              onOpenTeam={() => {
                router.push(`/student/competition/${competitionId}/team` as Href);
              }}
              onFindTeam={() => {
                router.push(`/student/competition/${competitionId}/team` as Href);
              }}
              onPressDeliverable={(deliverableId) => {
                router.push(
                  `/student/competition/${competitionId}/deliverables?deliverable=${deliverableId}` as Href,
                );
              }}
              onPressLeaderboard={() => {
                // Leaderboard renders inline on the dashboard; no-op for now.
                // Future: a /leaderboard sub-route could focus this.
              }}
              onPressAnnouncements={() => setAnnouncementsOverlayOpen(true)}
              onPressOneAnnouncement={(aid) => {
                setAnnouncementsOverlayOpen(true);
                void markAnnouncementViewed(aid).then(() => {
                  setAnnouncements((prev) =>
                    prev.map((a) => (a.id === aid ? { ...a, viewed: true } : a)),
                  );
                });
              }}
            />
          </View>
        ) : hasLoaded ? (
          // Not registered fallback — same content the legacy dashboard used.
          <View className="px-4 py-6 md:px-8">
            {actionErr ? (
              <Text className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {actionErr}
              </Text>
            ) : null}
            {eligible ? (
              <Button onPress={() => void onRegister()} loading={busy}>
                Register
              </Button>
            ) : (
              <Card variant="light" padding="md">
                <Text className="text-sm text-text/70">
                  You are not eligible to register for this competition.
                </Text>
              </Card>
            )}
          </View>
        ) : null}
      </ScrollView>

      <ProgramAnnouncementsOverlay
        visible={announcementsOverlayOpen}
        onClose={() => setAnnouncementsOverlayOpen(false)}
        competitionId={competitionId}
        competitionName={row.name}
        seed={announcements}
        onChanged={(next) => setAnnouncements(next)}
        canDelete={!!myTeam?.is_owner}
      />
    </>
  );
}

